/**
 * VibeScore — prompt-based 0–100 "vibe score"
 *
 * Two export modes:
 *  1. <VibeScore prompt="…" />         — full card (used after generation)
 *  2. <VibeScoreMini prompt="…" />     — compact inline badge (used in PromptInput live)
 *
 * Scoring is 100% client-side keyword analysis; no API calls.
 */

import React, { useMemo, useEffect, useRef, useState } from 'react';
import './VibeScore.css';

// ── Scoring engine ─────────────────────────────────────────────

const ENERGY_WORDS = [
  'fire', 'flame', 'explosion', 'dragon', 'warrior', 'fierce', 'rage',
  'thunder', 'lightning', 'storm', 'rage', 'epic', 'roar', 'battle',
  'neon', 'electric', 'blast', 'surge', 'volt', 'fury', 'wild', 'extreme',
  'power', 'force', 'burst', 'blazing', 'inferno', 'strike', 'collision',
  'glitch', 'cyberpunk', 'cosmic', 'stellar', 'galaxy', 'supernova',
  'holographic', 'laser', 'plasma', 'phantom', 'chaos', 'savage',
];

const UNIQUENESS_WORDS = [
  'psychedelic', 'surreal', 'ethereal', 'mystical', 'otherworldly',
  'bioluminescent', 'interdimensional', 'kaleidoscopic', 'fractal',
  'metaphysical', 'quantum', 'alchemical', 'transmutation', 'ancient',
  'sacred', 'eldritch', 'arcane', 'mythical', 'legendary', 'cryptic',
  'cybernetic', 'biomechanical', 'holographic', 'chromatic', 'iridescent',
  'translucent', 'crystalline', 'spectral', 'transcendent', 'luminous',
  'geomantic', 'hyperdimensional', 'zenith', 'vortex', 'paradox',
];

const STYLE_BONUS_WORDS = [
  'cinematic', 'dramatic', 'award-winning', 'detailed', 'masterful',
  'photorealistic', 'illustration', 'professional', 'stunning', 'rich',
  'gallery', 'vivid', 'dynamic', 'intricate', 'lush', 'vibrant',
  'hyper-detailed', 'ultra', 'majestic', 'breathtaking', 'gorgeous',
];

const CHILL_WORDS = [
  'zen', 'calm', 'serene', 'minimalist', 'minimal', 'simple', 'peaceful',
  'soft', 'gentle', 'pastel', 'mellow', 'quiet', 'tranquil', 'still',
];

// Vibe tiers: { min, max, label, emoji, color, gradientFrom, gradientTo, description }
const VIBE_TIERS = [
  {
    min: 0, max: 19,
    label: 'Sleepy', emoji: '😴',
    color: '#6b7280',
    gradientFrom: '#374151', gradientTo: '#6b7280',
    description: 'Very short or simple — try adding more details!',
  },
  {
    min: 20, max: 34,
    label: 'Chill', emoji: '😎',
    color: '#34d399',
    gradientFrom: '#065f46', gradientTo: '#34d399',
    description: 'Low-key and calm. Peaceful vibes.',
  },
  {
    min: 35, max: 49,
    label: 'Mellow', emoji: '🌊',
    color: '#38bdf8',
    gradientFrom: '#0c4a6e', gradientTo: '#38bdf8',
    description: 'Balanced energy. A solid foundation.',
  },
  {
    min: 50, max: 64,
    label: 'Alive', emoji: '✨',
    color: '#a78bfa',
    gradientFrom: '#4c1d95', gradientTo: '#a78bfa',
    description: 'Creative and expressive — looking good!',
  },
  {
    min: 65, max: 79,
    label: 'Electric', emoji: '⚡',
    color: '#fb923c',
    gradientFrom: '#7c2d12', gradientTo: '#fb923c',
    description: 'High energy with great visual potential.',
  },
  {
    min: 80, max: 89,
    label: 'Cosmic', emoji: '🌌',
    color: '#e879f9',
    gradientFrom: '#701a75', gradientTo: '#e879f9',
    description: 'Out of this world. Seriously cool prompt.',
  },
  {
    min: 90, max: 100,
    label: 'Legendary', emoji: '🔥',
    color: '#fbbf24',
    gradientFrom: '#78350f', gradientTo: '#fbbf24',
    description: 'Maximum vibe achieved. Absolute banger.',
  },
];

export function computeVibeScore(prompt) {
  if (!prompt || prompt.trim().length === 0) return 0;
  const text = prompt.toLowerCase();
  const words = text.split(/\s+/);
  const wordCount = words.length;

  // 1. Length score — rewards 8–30 words (max at 18+)
  const lengthScore = Math.min(25, wordCount < 3 ? 5 : wordCount < 6 ? 12 : Math.min(25, wordCount * 1.4));

  // 2. Energy words match (max 25 pts)
  const energyMatches = ENERGY_WORDS.filter(w => text.includes(w)).length;
  const energyScore = Math.min(25, energyMatches * 8);

  // 3. Uniqueness words (max 20 pts)
  const uniqueMatches = UNIQUENESS_WORDS.filter(w => text.includes(w)).length;
  const uniqueScore = Math.min(20, uniqueMatches * 7);

  // 4. Style bonus words (max 15 pts)
  const styleMatches = STYLE_BONUS_WORDS.filter(w => text.includes(w)).length;
  const styleScore = Math.min(15, styleMatches * 6);

  // 5. Chill modifier — chill prompts get a slight floor boost but cap out lower
  const chillMatches = CHILL_WORDS.filter(w => text.includes(w)).length;
  const chillBoost = Math.min(10, chillMatches * 4);

  // 6. Comma/adjective richness — more commas = more descriptors
  const commaCount = (text.match(/,/g) || []).length;
  const commaScore = Math.min(10, commaCount * 2.5);

  // 7. Penalize very short prompts
  const lengthPenalty = wordCount < 3 ? -10 : wordCount < 5 ? -5 : 0;

  const raw = lengthScore + energyScore + uniqueScore + styleScore + chillBoost + commaScore + lengthPenalty;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

export function getVibeTier(score) {
  return VIBE_TIERS.find(t => score >= t.min && score <= t.max) || VIBE_TIERS[0];
}

// ── Ring SVG component ────────────────────────────────────────

const RADIUS = 38;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function RingArc({ score, color, size = 96, strokeWidth = 8, animated = true }) {
  const displayRef = useRef(0);
  const [displayScore, setDisplayScore] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(0);

  // Animate score count-up
  useEffect(() => {
    if (!animated) { setDisplayScore(score); return; }
    const from = fromRef.current;
    const to = score;
    fromRef.current = score;
    startRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const duration = 700;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      const current = Math.round(from + (to - from) * eased);
      displayRef.current = current;
      setDisplayScore(current);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [score, animated]);

  const r = size / 2 - strokeWidth / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (displayScore / 100) * circ;

  return { displayScore, offset, circ, r };
}

// ── Full card ─────────────────────────────────────────────────

export default function VibeScore({ prompt }) {
  const score = useMemo(() => computeVibeScore(prompt), [prompt]);
  const tier = getVibeTier(score);

  const SIZE = 96;
  const SW = 8;
  const r = SIZE / 2 - SW / 2;
  const circ = 2 * Math.PI * r;

  // Animate score count-up
  const [displayScore, setDisplayScore] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(0);

  useEffect(() => {
    const from = fromRef.current;
    const to = score;
    fromRef.current = score;
    startRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const duration = 900;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayScore(Math.round(from + (to - from) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [score]);

  const offset = circ - (displayScore / 100) * circ;
  const currentTier = getVibeTier(displayScore);

  if (!prompt || prompt.trim().length === 0) return null;

  return (
    <div className="vibe-score-card">
      <div className="vibe-score-header">
        <span className="vibe-score-header-icon">🎯</span>
        Vibe Score
      </div>
      <div className="vibe-score-inner">
        {/* Ring */}
        <div className="vibe-score-ring-wrap">
          <svg viewBox={`0 0 ${SIZE} ${SIZE}`} xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="vibeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={tier.gradientFrom} />
                <stop offset="100%" stopColor={tier.gradientTo} />
              </linearGradient>
            </defs>
            <circle
              className="vibe-score-ring-bg"
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={r}
            />
            <circle
              className="vibe-score-ring-fill"
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={r}
              stroke="url(#vibeGrad)"
              strokeDasharray={circ}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="vibe-score-center">
            <span
              className="vibe-score-number"
              style={{ color: currentTier.color }}
            >
              {displayScore}
            </span>
            <span className="vibe-score-max">/100</span>
          </div>
        </div>

        {/* Label + description */}
        <div className="vibe-score-info">
          <div
            className="vibe-score-label"
            style={{ color: currentTier.color }}
          >
            {currentTier.emoji} {currentTier.label}
          </div>
          <div className="vibe-score-desc">
            {currentTier.description}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Mini badge (live in prompt area) ─────────────────────────

const MINI_SIZE = 22;
const MINI_SW = 3;
const MINI_R = MINI_SIZE / 2 - MINI_SW / 2;
const MINI_CIRC = 2 * Math.PI * MINI_R;

export function VibeScoreMini({ prompt }) {
  const score = useMemo(() => computeVibeScore(prompt), [prompt]);
  const tier = getVibeTier(score);

  const [displayScore, setDisplayScore] = useState(score);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(score);

  useEffect(() => {
    const from = fromRef.current;
    const to = score;
    fromRef.current = score;
    startRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const duration = 500;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayScore(Math.round(from + (to - from) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [score]);

  const offset = MINI_CIRC - (displayScore / 100) * MINI_CIRC;
  const currentTier = getVibeTier(displayScore);

  if (!prompt || prompt.trim().length < 3) return null;

  return (
    <span
      className="vibe-score-mini"
      title={`Vibe Score: ${score}/100 — ${tier.label}`}
      style={{
        borderColor: currentTier.color + '55',
        background: currentTier.color + '12',
        color: currentTier.color,
      }}
    >
      {/* Mini ring */}
      <span className="vibe-mini-ring">
        <svg viewBox={`0 0 ${MINI_SIZE} ${MINI_SIZE}`} xmlns="http://www.w3.org/2000/svg">
          <circle
            className="vibe-mini-ring-bg"
            cx={MINI_SIZE / 2}
            cy={MINI_SIZE / 2}
            r={MINI_R}
            stroke={currentTier.color}
          />
          <circle
            className="vibe-mini-ring-fill"
            cx={MINI_SIZE / 2}
            cy={MINI_SIZE / 2}
            r={MINI_R}
            stroke={currentTier.color}
            strokeDasharray={MINI_CIRC}
            strokeDashoffset={offset}
          />
        </svg>
      </span>
      <span className="vibe-mini-num">{displayScore}</span>
      <span>{currentTier.emoji} {currentTier.label}</span>
    </span>
  );
}
